<template>
  <div class="qf-table-row" :class="{ 'active-row': activeRowKeys.includes(row?.[rowKey]), 'error-row': errorRowList?.includes(row?.[rowKey]), 'row-checkboxed': checkedParams.checked?.includes(row?.id) }" :style="{ zIndex: rowZIdex }" @click="handleRowClick($event)">
    <div class="qf-table-fixed-left" ref="qfTableFixedLeftRef" v-if="hasLeftColumns" :class="{ 'active-row': activeRowKeys == row?.[rowKey], 'error-row': errorRowList?.includes(row?.[rowKey]), 'scroll-left-shadow': scrollShadowLeft, 'row-checkboxed': checkedParams.checked?.includes(row?.id) }">
      <div class="qf-table-row-placeholder" v-for="item in isGroup - 1" :key="item"></div>
      <div class="corner-mark" v-if="cornerMark">
        <slot name="cornerMark" :row="row"></slot>
      </div>
      <div class="qf-table-row-checkbox" :class="checkBoxClassName" v-if="checkedParams.enabled" :style="{ width: checkedParams.width + 'px' }" @click.stop="">
        <slot name="customCheckbox" :row="row" :index="rowIndex" :change="handleChange">
          <jw-checkbox :model-value="idLists?.includes(row?.[rowKey])" @change="handleChange($event, row?.[rowKey])"></jw-checkbox>
        </slot>
      </div>
      <qf-table-cell-group v-for="(column, colIndex) in colLeft" :key="column.colKey" :column="column" :row="row" :colIndex="colIndex">
        <template v-for="(_, name) in $slots" #[name]="slotProps" :key="name">
          <slot :name="name" v-bind="slotProps"></slot>
        </template>
        <slot :name="`table-cell-suffix-${colIndex}-${colIndex}`" :row="row" :width="column.width"></slot>
      </qf-table-cell-group>
    </div>
    <div class="qf-table-content" :class="{ 'row-checkboxed': checkedParams.checked?.includes(row?.id) }" v-if="colMiddle?.length" ref="qfTableContentRef">
      <template v-for="(column, colIndex) in colMiddle" :key="column.colKey">
        <div v-if="column.children?.length" class="sub-form-body">
          <qf-table-cell-group v-for="(group, groupIndex) in column.children" :key="group.colKey" :column="group" :row="row" :colIndex="groupIndex">
            <template v-for="(_, name) in $slots" #[name]="slotProps" :key="name">
              <slot :name="name" v-bind="slotProps"></slot>
            </template>
            <slot :name="`table-cell-suffix-${colLeft?.length + colIndex}-${groupIndex}`" :row="row" :width="group.width"></slot>
          </qf-table-cell-group>
        </div>
        <qf-table-cell-group v-else :column="column" :row="row" :colIndex="colIndex">
          <template v-for="(_, name) in $slots" #[name]="slotProps" :key="name">
            <slot :name="name" v-bind="slotProps"></slot>
          </template>
          <slot :name="`table-cell-suffix-${colLeft?.length + colIndex}-${colLeft?.length + colIndex}`" :row="row" :width="column.width"></slot>
        </qf-table-cell-group>
      </template>
    </div>
    <div class="qf-table-fixed-right" v-if="colRight?.length" ref="qfTableFixedRightRef" :class="{ 'active-row': activeRowKeys == row?.[rowKey], 'error-row': errorRowList?.includes(row?.[rowKey]), 'scroll-right-shadow': scrollShadowRight, 'row-checkboxed': checkedParams.checked?.includes(row?.id) }">
      <qf-table-cell-group v-for="(column, colIndex) in colRight" :key="column.colKey" :column="column" :row="row" :colIndex="colIndex">
        <template v-for="(_, name) in $slots" #[name]="slotProps" :key="name">
          <slot :name="name" v-bind="slotProps"></slot>
        </template>
      </qf-table-cell-group>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, onUnmounted, ref } from 'vue';
import QfTableCellGroup from '../qf-table-cell-group/qf-table-cell-group.vue';
import { tools } from '@/utils/tools';
const props = defineProps({
  row: {
    type: Object,
    default: () => {}
  },
  rowIndex: {
    type: Number,
    default: 0
  },
  columns: {
    type: Array,
    default: () => []
  },
  rowKey: {
    type: String,
    default: 'id'
  },
  hoveredRow: {
    type: Number,
    default: 0
  },
  expandedRowKeys: {
    type: Array,
    default: () => []
  },
  checkedParams: {
    type: Object,
    default: () => ({
      enabled: true,
      type: 'checkbox',
      width: 40,
      checked: []
    })
  },
  activeRowKeys: {
    type: Array,
    default: () => []
  },
  errorRowList: {
    type: Array,
    default: () => []
  },
  checkBoxClassName: {
    type: String,
    default: ''
  },
  isGroup: {
    type: Number,
    default: 0
  },
  cornerMark: {
    type: Boolean,
    default: false
  }
});
const randomKey = ref('');
const idLists = ref(inject('qfTable')?.idList);
const scrollShadowLeft = ref(inject('qfTable')?.scrollShadowLeft);
const scrollShadowRight = ref(inject('qfTable')?.scrollShadowRight);
const emits = defineEmits(['rowClick', 'handleChecked']);
const qfTableFixedLeftRef = ref(null);
const qfTableContentRef = ref(null);
const qfTableFixedRightRef = ref(null);

// 刷新行数据
const refreshRow = (rk) => {
  if (rk == props.row?.[props.rowKey]) {
    randomKey.value = tools.randomString(6);
  }
};
// 优化行点击逻辑
const handleRowClick = (e) => {
  emits('rowClick', { e, row: props.row });
};

// 优化单选逻辑
const handleChange = (value, id) => {
  emits('handleChecked', value, id);
};
// 计算列的固定位置
const colLeft = computed(() => props.columns?.filter((v) => v.fixed === 'left'));
const colMiddle = computed(() => props.columns?.filter((v) => v.fixed !== 'left' && v.fixed !== 'right'));
const colRight = computed(() => props.columns?.filter((v) => v.fixed === 'right'));

// 是否有左固定列
const hasLeftColumns = computed(() => colLeft.value?.length || props.checkedParams.enabled || props.cornerMark);
const rowZIdex = ref(null);
// 提供修改行数据的z-index方法
const updateZIndex = (zIndex) => {
  rowZIdex.value = zIndex;
};
onUnmounted(() => {
  qfTableFixedLeftRef.value = null;
  qfTableContentRef.value = null;
  qfTableFixedRightRef.value = null;
  randomKey.value = null;
});
defineExpose({ refreshRow, updateZIndex });
</script>

<style scoped lang="less">
@import './qf-table-row-group.less';
</style>
